import { useState, useEffect } from 'react'
import { Box, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material'
import { CandleTarget } from '../../contexts/PatternTreeContext'

type CandleMode = 'current' | 'previous' | 'range'

interface CandleTargetSelectProps {
  candle?: CandleTarget
  onChange: (candle: CandleTarget | undefined) => void
  disabled?: boolean
  hideRange?: boolean
  label?: string
}

function getCandleMode(candle?: CandleTarget): CandleMode {
  if (!candle || (candle.offset === 0 && !candle.lookback)) return 'current'
  if (candle.lookback) return 'range'
  return 'previous'
}

const CandleTargetSelect: React.FC<CandleTargetSelectProps> = ({
  candle,
  onChange,
  disabled = false,
  hideRange = false,
  label = 'Candle',
}) => {
  const [mode, setMode] = useState<CandleMode>(getCandleMode(candle))

  useEffect(() => {
    setMode(getCandleMode(candle))
  }, [candle])

  const handleModeChange = (newMode: CandleMode) => {
    setMode(newMode)
    switch (newMode) {
      case 'current':
        onChange(undefined)
        break
      case 'previous':
        onChange({ offset: 1 })
        break
      case 'range':
        onChange({ offset: 0, lookback: { count: 5, mode: 'all' } })
        break
    }
  }

  const handleOffsetChange = (offset: number) => {
    if (offset < 1) offset = 1
    if (offset > 50) offset = 50
    onChange({ offset })
  }

  const handleLookbackCountChange = (count: number) => {
    if (count < 1) count = 1
    if (count > 50) count = 50
    const lookback = candle?.lookback || { count: 5, mode: 'all' as const }
    onChange({
      offset: candle?.offset ?? 0,
      lookback: { ...lookback, count },
    })
  }

  const handleLookbackModeChange = (lookbackMode: 'all' | 'some') => {
    const lookback = candle?.lookback || { count: 5, mode: 'all' as const }
    const updated = { ...lookback, mode: lookbackMode }
    if (lookbackMode === 'all') {
      delete updated.needed
    } else if (!updated.needed) {
      updated.needed = 1
    }
    onChange({
      offset: candle?.offset ?? 0,
      lookback: updated,
    })
  }

  const handleLookbackNeededChange = (needed: number) => {
    const lookback = candle?.lookback || { count: 5, mode: 'some' as const }
    if (needed < 1) needed = 1
    if (needed >= lookback.count) needed = lookback.count - 1
    onChange({
      offset: candle?.offset ?? 0,
      lookback: { ...lookback, needed },
    })
  }

  return (
    <Box display="flex" flexDirection="column" gap={0.5}>
      <Box display="flex" gap={0.5} alignItems="center">
        <FormControl size="small" disabled={disabled} sx={{ minWidth: 130 }}>
          <InputLabel
            sx={{
              '&:not(.MuiInputLabel-shrink)': {
                transform: 'translate(14px, 6px) scale(0.85)',
              },
            }}
          >
            {label}
          </InputLabel>
          <Select
            value={mode}
            label={label}
            onChange={(e) => handleModeChange(e.target.value as CandleMode)}
            sx={{ height: 32, fontSize: '0.8rem' }}
          >
            <MenuItem value="current">Current</MenuItem>
            <MenuItem value="previous">Previous Nth</MenuItem>
            {!hideRange && <MenuItem value="range">Previous Range</MenuItem>}
          </Select>
        </FormControl>

        {mode === 'previous' && (
          <TextField
            size="small"
            type="number"
            value={candle?.offset ?? 1}
            onChange={(e) => handleOffsetChange(parseInt(e.target.value) || 1)}
            inputProps={{ min: 1, max: 50 }}
            disabled={disabled}
            sx={{ width: 56, '& .MuiInputBase-root': { height: 32, fontSize: '0.8rem' } }}
          />
        )}

        {mode === 'range' && (
          <TextField
            size="small"
            type="number"
            value={candle?.lookback?.count ?? 5}
            onChange={(e) => handleLookbackCountChange(parseInt(e.target.value) || 1)}
            inputProps={{ min: 1, max: 50 }}
            disabled={disabled}
            sx={{ width: 56, '& .MuiInputBase-root': { height: 32, fontSize: '0.8rem' } }}
          />
        )}
      </Box>

      {mode === 'range' && (
        <Box display="flex" gap={0.5} alignItems="center">
          <FormControl size="small" disabled={disabled} sx={{ minWidth: 130 }}>
            <Select
              value={candle?.lookback?.mode ?? 'all'}
              onChange={(e) => handleLookbackModeChange(e.target.value as 'all' | 'some')}
              sx={{ height: 32, fontSize: '0.8rem' }}
            >
              <MenuItem value="all">All must match</MenuItem>
              <MenuItem value="some">At least</MenuItem>
            </Select>
          </FormControl>

          {candle?.lookback?.mode === 'some' && (
            <TextField
              size="small"
              type="number"
              value={candle?.lookback?.needed ?? 1}
              onChange={(e) => handleLookbackNeededChange(parseInt(e.target.value) || 1)}
              inputProps={{ min: 1, max: (candle?.lookback?.count ?? 5) - 1 }}
              disabled={disabled}
              sx={{ width: 56, '& .MuiInputBase-root': { height: 32, fontSize: '0.8rem' } }}
            />
          )}
        </Box>
      )}
    </Box>
  )
}

export default CandleTargetSelect
